import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/itemCodeCreationAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

const isSubmited = (status) => status === 'Submited' || status === 'Submitted';

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCategoryList = createAsyncThunk(
    'itemCodeCreation/fetchCategoryList',
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.getItemCategoryDetails();
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load categories'));
        }
    }
);

export const fetchUnitsList = createAsyncThunk(
    'itemCodeCreation/fetchUnitsList',
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.getAllUnits();
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load units'));
        }
    }
);

export const fetchDcaList = createAsyncThunk(
    'itemCodeCreation/fetchDcaList',
    async ({ categoryId }, { rejectWithValue }) => {
        try {
            const res = await api.getDcas(categoryId);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Please Configure DCAs'));
        }
    }
);

export const fetchSDcaList = createAsyncThunk(
    'itemCodeCreation/fetchSDcaList',
    async ({ dcaCode }, { rejectWithValue }) => {
        try {
            const res = await api.getSDcas(dcaCode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load sub DCAs'));
        }
    }
);

export const fetchHsnList = createAsyncThunk(
    'itemCodeCreation/fetchHsnList',
    async ({ categoryId }, { rejectWithValue }) => {
        try {
            const res = await api.getHsn(categoryId);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load HSN codes'));
        }
    }
);

export const fetchHsnRemarks = createAsyncThunk(
    'itemCodeCreation/fetchHsnRemarks',
    async ({ hsnId }, { rejectWithValue }) => {
        try {
            const res = await api.getHsnDesc(hsnId);
            return res?.Data?.[0]?.HSNRemarks || '';
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load HSN remarks'));
        }
    }
);

export const fetchMajorGroupList = createAsyncThunk(
    'itemCodeCreation/fetchMajorGroupList',
    async ({ val, txt }, { rejectWithValue }) => {
        try {
            const res = await api.getMajougroupcode(val, txt);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load major groups'));
        }
    }
);

export const fetchSubGroupList = createAsyncThunk(
    'itemCodeCreation/fetchSubGroupList',
    async ({ cat, grp }, { rejectWithValue }) => {
        try {
            const res = await api.getMajorsubgroups(cat, grp);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load sub groups'));
        }
    }
);

export const fetchSpecificationCodes = createAsyncThunk(
    'itemCodeCreation/fetchSpecificationCodes',
    async ({ cat, mGrp, sGrp }, { rejectWithValue }) => {
        try {
            const res = await api.getSpecificationcode(cat, mGrp, sGrp);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load specification codes'));
        }
    }
);

export const fetchConnectedSDcaList = createAsyncThunk(
    'itemCodeCreation/fetchConnectedSDcaList',
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.getConnectedSDcas();
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load connected sub DCAs'));
        }
    }
);

// Success format from the API is "Submited" — anything else is an error message
// meant to be shown verbatim.
export const addItemCategory = createAsyncThunk(
    'itemCodeCreation/addItemCategory',
    async (payload, { dispatch, rejectWithValue }) => {
        try {
            const res = await api.saveItemCategory(payload);
            const status = res?.Data || '';
            if (!isSubmited(status)) return rejectWithValue(status || 'Failed to add category');
            const refreshed = await api.getItemCategoryDetailsFill();
            dispatch(setCategoryList(refreshed?.Data || []));
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to add category'));
        }
    }
);

export const addMajorGroup = createAsyncThunk(
    'itemCodeCreation/addMajorGroup',
    async (payload, { dispatch, rejectWithValue }) => {
        try {
            const res = await api.saveMajorGroup(payload);
            const status = res?.Data || '';
            if (!isSubmited(status)) return rejectWithValue(status || 'Failed to add major group');
            // Refresh with the category-filtered list. Legacy reloaded spGetMajorCategoryDetails (every
            // category's groups), which let a group from another category be picked — spSaveItemCodeDetails
            // then can't find the ItemcodeCreation row (CategoryCode+CategoryName+MajorgroupCode) and saves a NULL CategoryName.
            const refreshed = await api.getMajougroupcode(payload.ItemCategoryCode, payload.ItemCategoryName);
            dispatch(setMajorGroupList(refreshed?.Data || []));
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to add major group'));
        }
    }
);

export const addUnit = createAsyncThunk(
    'itemCodeCreation/addUnit',
    async (payload, { dispatch, rejectWithValue }) => {
        try {
            const res = await api.saveUnits(payload);
            const status = res?.Data || '';
            if (!isSubmited(status)) return rejectWithValue(status || 'Failed to add unit');
            const refreshed = await api.getAllUnits();
            dispatch(setUnitsList(refreshed?.Data || []));
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to add unit'));
        }
    }
);

// Success value from the API is "Successfull" — anything else is an error message
// meant to be shown verbatim.
export const submitItemCode = createAsyncThunk(
    'itemCodeCreation/submitItemCode',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveItemCodeDetails(payload);
            const status = res?.Data || '';
            if (status !== 'Successfull') {
                return rejectWithValue(status || 'Failed to submit item code');
            }
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit item code'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    categoryList: [],
    unitsList: [],
    dcaList: [],
    sdcaList: [],
    hsnList: [],
    hsnRemarks: '',
    majorGroupList: [],
    subGroupList: [],
    specCodeList: [],
    connectedSdcaList: [],
    submitResult: null,
    loading: {
        categoryList: false, unitsList: false, dcaList: false, sdcaList: false,
        hsnList: false, hsnRemarks: false, majorGroupList: false, subGroupList: false,
        specCodeList: false, connectedSdcaList: false,
        addCategory: false, addMajorGroup: false, addUnit: false, submit: false,
    },
    errors: {
        categoryList: null, unitsList: null, dcaList: null, sdcaList: null,
        hsnList: null, majorGroupList: null, subGroupList: null, connectedSdcaList: null,
        addCategory: null, addMajorGroup: null, addUnit: null, submit: null,
    },
};

const itemCodeCreationSlice = createSlice({
    name: 'itemCodeCreation',
    initialState,
    reducers: {
        setCategoryList:    (s, a) => { s.categoryList = a.payload; },
        setMajorGroupList:  (s, a) => { s.majorGroupList = a.payload; },
        setUnitsList:       (s, a) => { s.unitsList = a.payload; },
        clearDcaCascade:    (s) => { s.dcaList = []; s.sdcaList = []; s.hsnList = []; s.hsnRemarks = ''; s.majorGroupList = []; s.subGroupList = []; s.specCodeList = []; s.connectedSdcaList = []; },
        clearSDcaList:      (s) => { s.sdcaList = []; },
        clearHsnRemarks:    (s) => { s.hsnRemarks = ''; },
        clearSubGroupList:  (s) => { s.subGroupList = []; s.specCodeList = []; },
        clearSpecCodeList:  (s) => { s.specCodeList = []; },
        clearSubmitResult:  (s) => { s.submitResult = null; s.errors.submit = null; },
        resetItemCodeCreation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCategoryList.pending,   (s) => { s.loading.categoryList = true; s.errors.categoryList = null; })
            .addCase(fetchCategoryList.fulfilled, (s, a) => { s.loading.categoryList = false; s.categoryList = a.payload; })
            .addCase(fetchCategoryList.rejected,  (s, a) => { s.loading.categoryList = false; s.errors.categoryList = a.payload; });

        builder
            .addCase(fetchUnitsList.pending,   (s) => { s.loading.unitsList = true; s.errors.unitsList = null; })
            .addCase(fetchUnitsList.fulfilled, (s, a) => { s.loading.unitsList = false; s.unitsList = a.payload; })
            .addCase(fetchUnitsList.rejected,  (s, a) => { s.loading.unitsList = false; s.errors.unitsList = a.payload; });

        builder
            .addCase(fetchDcaList.pending,   (s) => { s.loading.dcaList = true; s.errors.dcaList = null; })
            .addCase(fetchDcaList.fulfilled, (s, a) => { s.loading.dcaList = false; s.dcaList = a.payload; })
            .addCase(fetchDcaList.rejected,  (s, a) => { s.loading.dcaList = false; s.errors.dcaList = a.payload; s.dcaList = []; });

        builder
            .addCase(fetchSDcaList.pending,   (s) => { s.loading.sdcaList = true; s.errors.sdcaList = null; })
            .addCase(fetchSDcaList.fulfilled, (s, a) => { s.loading.sdcaList = false; s.sdcaList = a.payload; })
            .addCase(fetchSDcaList.rejected,  (s, a) => { s.loading.sdcaList = false; s.errors.sdcaList = a.payload; s.sdcaList = []; });

        builder
            .addCase(fetchHsnList.pending,   (s) => { s.loading.hsnList = true; s.errors.hsnList = null; })
            .addCase(fetchHsnList.fulfilled, (s, a) => { s.loading.hsnList = false; s.hsnList = a.payload; })
            .addCase(fetchHsnList.rejected,  (s, a) => { s.loading.hsnList = false; s.errors.hsnList = a.payload; s.hsnList = []; });

        builder
            .addCase(fetchHsnRemarks.pending,   (s) => { s.loading.hsnRemarks = true; })
            .addCase(fetchHsnRemarks.fulfilled, (s, a) => { s.loading.hsnRemarks = false; s.hsnRemarks = a.payload; })
            .addCase(fetchHsnRemarks.rejected,  (s) => { s.loading.hsnRemarks = false; s.hsnRemarks = ''; });

        builder
            .addCase(fetchMajorGroupList.pending,   (s) => { s.loading.majorGroupList = true; s.errors.majorGroupList = null; })
            .addCase(fetchMajorGroupList.fulfilled, (s, a) => { s.loading.majorGroupList = false; s.majorGroupList = a.payload; })
            .addCase(fetchMajorGroupList.rejected,  (s, a) => { s.loading.majorGroupList = false; s.errors.majorGroupList = a.payload; s.majorGroupList = []; });

        builder
            .addCase(fetchSubGroupList.pending,   (s) => { s.loading.subGroupList = true; s.errors.subGroupList = null; })
            .addCase(fetchSubGroupList.fulfilled, (s, a) => { s.loading.subGroupList = false; s.subGroupList = a.payload; })
            .addCase(fetchSubGroupList.rejected,  (s, a) => { s.loading.subGroupList = false; s.errors.subGroupList = a.payload; s.subGroupList = []; });

        builder
            .addCase(fetchSpecificationCodes.pending,   (s) => { s.loading.specCodeList = true; })
            .addCase(fetchSpecificationCodes.fulfilled, (s, a) => { s.loading.specCodeList = false; s.specCodeList = a.payload; })
            .addCase(fetchSpecificationCodes.rejected,  (s) => { s.loading.specCodeList = false; s.specCodeList = []; });

        builder
            .addCase(fetchConnectedSDcaList.pending,   (s) => { s.loading.connectedSdcaList = true; s.errors.connectedSdcaList = null; })
            .addCase(fetchConnectedSDcaList.fulfilled, (s, a) => { s.loading.connectedSdcaList = false; s.connectedSdcaList = a.payload; })
            .addCase(fetchConnectedSDcaList.rejected,  (s, a) => { s.loading.connectedSdcaList = false; s.errors.connectedSdcaList = a.payload; s.connectedSdcaList = []; });

        builder
            .addCase(addItemCategory.pending,   (s) => { s.loading.addCategory = true; s.errors.addCategory = null; })
            .addCase(addItemCategory.fulfilled, (s) => { s.loading.addCategory = false; })
            .addCase(addItemCategory.rejected,  (s, a) => { s.loading.addCategory = false; s.errors.addCategory = a.payload; });

        builder
            .addCase(addMajorGroup.pending,   (s) => { s.loading.addMajorGroup = true; s.errors.addMajorGroup = null; })
            .addCase(addMajorGroup.fulfilled, (s) => { s.loading.addMajorGroup = false; })
            .addCase(addMajorGroup.rejected,  (s, a) => { s.loading.addMajorGroup = false; s.errors.addMajorGroup = a.payload; });

        builder
            .addCase(addUnit.pending,   (s) => { s.loading.addUnit = true; s.errors.addUnit = null; })
            .addCase(addUnit.fulfilled, (s) => { s.loading.addUnit = false; })
            .addCase(addUnit.rejected,  (s, a) => { s.loading.addUnit = false; s.errors.addUnit = a.payload; });

        builder
            .addCase(submitItemCode.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitItemCode.fulfilled, (s) => { s.loading.submit = false; s.submitResult = true; })
            .addCase(submitItemCode.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    setCategoryList,
    setMajorGroupList,
    setUnitsList,
    clearDcaCascade,
    clearSDcaList,
    clearHsnRemarks,
    clearSubGroupList,
    clearSpecCodeList,
    clearSubmitResult,
    resetItemCodeCreation,
} = itemCodeCreationSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectICCCategoryList      = (s) => s.itemCodeCreation.categoryList;
export const selectICCUnitsList         = (s) => s.itemCodeCreation.unitsList;
export const selectICCDcaList           = (s) => s.itemCodeCreation.dcaList;
export const selectICCSDcaList          = (s) => s.itemCodeCreation.sdcaList;
export const selectICCHsnList           = (s) => s.itemCodeCreation.hsnList;
export const selectICCHsnRemarks        = (s) => s.itemCodeCreation.hsnRemarks;
export const selectICCMajorGroupList    = (s) => s.itemCodeCreation.majorGroupList;
export const selectICCSubGroupList      = (s) => s.itemCodeCreation.subGroupList;
export const selectICCSpecCodeList      = (s) => s.itemCodeCreation.specCodeList;
export const selectICCConnectedSdcaList = (s) => s.itemCodeCreation.connectedSdcaList;
export const selectICCSubmitResult      = (s) => s.itemCodeCreation.submitResult;
export const selectICCLoading           = (s) => s.itemCodeCreation.loading;
export const selectICCErrors            = (s) => s.itemCodeCreation.errors;

export default itemCodeCreationSlice.reducer;
